import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Phone, Mail, MapPin, Clock, Send } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent! We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#FAFAFA] min-h-screen">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 grid lg:grid-cols-2 gap-14">
        <div>
          <span className="text-xs font-semibold uppercase tracking-[0.22em] text-[#8FA38C]">Get in touch</span>
          <h1 className="mt-4 font-heading text-4xl sm:text-5xl font-semibold text-[#1A362D] tracking-tight">
            Let's talk about your space
          </h1>
          <p className="mt-5 text-[#4A5D4E] text-lg font-light">
            Have a question or ready for a quote? Send us a message and we'll respond within one business day.
          </p>

          <div className="mt-10 space-y-6">
            {[
              { icon: Phone, label: "Phone", value: "403-690-0222" },
              { icon: Mail, label: "Email", value: "itsjaspreet4u@gmail.com" },
              { icon: MapPin, label: "Service Area", value: "Calgary · Airdrie · Okotoks, AB" },
              { icon: Clock, label: "Hours", value: "Mon–Sat · 7:00 AM – 7:00 PM" },
            ].map((c) => (
              <div key={c.label} className="flex items-start gap-4">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#1A362D] text-white shrink-0">
                  <c.icon className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-xs uppercase tracking-widest text-[#8FA38C]">{c.label}</p>
                  <p className="text-[#1A362D] font-medium">{c.value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-[#E2E8E4]/60 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8 md:p-10">
          <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
            <div>
              <Label className="text-[#1A362D]">Name</Label>
              <Input data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Doe" className="mt-1.5 bg-[#FAFAFA] border-[#E2E8E4]" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-[#1A362D]">Email</Label>
                <Input data-testid="contact-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jane@email.com" className="mt-1.5 bg-[#FAFAFA] border-[#E2E8E4]" />
              </div>
              <div>
                <Label className="text-[#1A362D]">Phone</Label>
                <Input data-testid="contact-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(555) 000-0000" className="mt-1.5 bg-[#FAFAFA] border-[#E2E8E4]" />
              </div>
            </div>
            <div>
              <Label className="text-[#1A362D]">Message</Label>
              <Textarea data-testid="contact-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Tell us about your project…" rows={5} className="mt-1.5 bg-[#FAFAFA] border-[#E2E8E4]" />
            </div>
            <Button type="submit" disabled={loading} data-testid="contact-submit" className="w-full bg-[#C47B57] hover:bg-[#D58C68] text-white rounded-full py-6 text-base">
              {loading ? "Sending…" : <>Send Message <Send className="ml-2 h-4 w-4" /></>}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
}
